import { useState, type ReactNode } from 'react';
import type { Session } from '../lib/session';
import { allowedTabs, isOwner, lock } from '../lib/session';
import { useIsPhone } from '../lib/viewport';
import TabIcon from './TabIcon';
import SyncBanner from './SyncBanner';
import MySignature from './MySignature';

/**
 * Header and navigation.
 *
 *   header   brand with the status dot, a Settings button for the owner, and the
 *            name chip on the right. "Tapping the name chip locks it by hand at
 *            shift change" — so the chip is the lock button, as it is in the
 *            prototype (`data-act="lock"`).
 *   tabs     built by role and permission.
 *
 * Two sizes. Under 700px "navigation moves to a bottom bar with icons", where
 * Settings joins the tabs rather than crowding the header. From 700px up the
 * tabs sit across the top at every width — the left rail that used to appear
 * above 1180px cost the table its room and went on 23 September 2026. Only
 * the bottom bar changes the markup — the rest is CSS.
 */

interface Props {
  session: Session;
  tab: string;
  onTab: (tab: string) => void;
  onSettings: () => void;
  settingsOpen: boolean;
  children: ReactNode;
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export default function Shell({
  session, tab, onTab, onSettings, settingsOpen, children,
}: Props) {
  const tabs = allowedTabs(session);
  const owner = isOwner(session);
  const phone = useIsPhone();
  const [signing, setSigning] = useState(false);

  return (
    <>
      <header className="app-header">
        <div className="hrow">
          <div className="brand">
            <span
              className="dot"
              style={session.offline ? { background: 'var(--text-warning)' } : undefined}
              title={session.offline ? 'Working offline' : 'Connected'}
            />
            <span className="brand-full">Quarry cost tracker</span>
            <span className="brand-short">Quarry</span>
          </div>

          {/* Everyone draws their signature once; it is stamped on what they
              prepare or approve. Not in Settings: a clerk has no Settings. */}
          <button
            type="button"
            className="btn sm"
            style={{ minWidth: 0 }}
            title="My signature"
            onClick={() => setSigning(true)}
          >
            {phone ? 'Signature' : 'My signature'}
          </button>
          {signing && <MySignature onClose={() => setSigning(false)} />}

          {/* On a phone Settings is the last item in the bottom bar instead —
              the header has no room for it beside the name chip. */}
          {owner && !phone && (
            <button
              type="button"
              className="btn sm"
              style={{ minWidth: 0 }}
              title="Settings"
              onClick={onSettings}
            >
              Settings
            </button>
          )}

          <button
            type="button"
            className="who"
            onClick={() => void lock()}
            title="Lock the tablet"
          >
            <span className="who-name">
              {session.user.name}
              {owner && ' · owner'}
            </span>
            <span className="avatar">{initials(session.user.name)}</span>
          </button>
        </div>
      </header>

      <SyncBanner session={session} />

      <div id="shell">
        {/* Top tabs / left rail. Hidden on a phone, where the bottom bar takes over. */}
        <div className="tabs">
          {tabs.map((definition) => (
            <button
              key={definition.id}
              type="button"
              className={tab === definition.id && !settingsOpen ? 'on' : ''}
              onClick={() => onTab(definition.id)}
            >
              {definition.label}
            </button>
          ))}
        </div>

        <div className="wrap">{children}</div>
      </div>

      {phone && (
        <nav className="bottomnav" aria-label="Sections">
          {tabs.map((definition) => (
            <button
              key={definition.id}
              type="button"
              className={tab === definition.id && !settingsOpen ? 'on' : ''}
              onClick={() => onTab(definition.id)}
            >
              <TabIcon name={definition.id} />
              <span>{definition.label}</span>
            </button>
          ))}
          {owner && (
            <button
              type="button"
              className={settingsOpen ? 'on' : ''}
              onClick={onSettings}
            >
              <TabIcon name="settings" />
              <span>Settings</span>
            </button>
          )}
        </nav>
      )}
    </>
  );
}
